import React, { useState, useEffect, useRef } from "react";
import { PersonSquare } from "react-bootstrap-icons";
import { Spinner } from "react-bootstrap";
import { useCustomerAvatarUrl } from "../hooks/useCustomerAvatarUrl";

export { invalidateCustomerAvatarCache } from "../utils/customerAvatarCache";
export { fetchCustomerAvatarImageUrl, refreshCustomerAvatarCache } from "../utils/customerAvatarLoader";

/**
 * Lazy-loads a customer profile picture when scrolled into view.
 * Resolves avatar via GET /customers/avatar and renders a static JPG URL.
 * Re-fetches when refreshCustomerAvatarCache() bumps the revision (e.g. after upload).
 */
const CustomerAvatar = ({
  customerId,
  alt = "",
  size = 64,
  className,
  style,
  onClick,
  lazy = true,
  borderRadius = "8px",
}) => {
  const rootRef = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(!lazy);
  const { imageUrl, loading } = useCustomerAvatarUrl(customerId, {
    enabled: Boolean(customerId && shouldLoad),
  });

  useEffect(() => {
    if (!lazy || !customerId) {
      if (!lazy) setShouldLoad(true);
      return undefined;
    }
    const node = rootRef.current;
    if (!node) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setShouldLoad(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: "120px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [lazy, customerId]);

  const commonStyle = {
    width: size,
    height: size,
    borderRadius,
    flexShrink: 0,
    ...style,
  };

  if (imageUrl) {
    return (
      <span ref={rootRef} style={{ display: "inline-flex", lineHeight: 0 }}>
        <img
          src={imageUrl}
          alt={alt}
          loading="lazy"
          decoding="async"
          onClick={onClick}
          className={className}
          style={{
            ...commonStyle,
            objectFit: "cover",
            cursor: onClick ? "pointer" : undefined,
          }}
        />
      </span>
    );
  }

  return (
    <span
      ref={rootRef}
      onClick={onClick}
      className={className}
      style={{
        ...commonStyle,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: onClick ? "pointer" : undefined,
      }}
    >
      {loading ? (
        <Spinner animation="border" size="sm" />
      ) : (
        <PersonSquare size={size} style={{ color: "#ccc" }} />
      )}
    </span>
  );
};

export default CustomerAvatar;
